import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../Client'

// Eén lopende/afgeronde aanroep per portfolio, gedeeld over de sessie. Op een
// portfoliodashboard lezen drie tegels dit antwoord (Maatschappelijke waarde,
// Bespaarde CO2, Bespaard op energierekening); zonder deze cache doet elk een
// eigen Cloud Function-aanroep plus een read per project. Bij een fout wist de
// cache zichzelf, zodat een volgende poging het wél weer probeert.
// Zelfde recept als useOverviewCounts en fetchStandaardCatalogs.
const inFlight = new Map()

/**
 * @param {string} portfolioId
 * @returns {Promise<{data: object}>}
 */
const fetchValue = (portfolioId) => {
    if (!inFlight.has(portfolioId)) {
        inFlight.set(
            portfolioId,
            firebase.app().functions('europe-west1')
                .httpsCallable('getPortfolioSocietalValue')({ client: portfolioId })
                .catch((error) => {
                    inFlight.delete(portfolioId)
                    throw error
                })
        )
    }
    return inFlight.get(portfolioId)
}

/**
 * @typedef {object} PortfolioProjectValue
 * @property {string} CompagnyID - Tenant van het project
 * @property {string} Name - Naam van het project; '' als die ontbreekt
 * @property {number} Participation - Waarde uit de participatieladder
 * @property {number} Msi - Waarde uit de monetarisering van meetstandaard-effecten
 * @property {number} Materials - Maatschappelijke besparing van materialen en adviezen
 * @property {number} Total - De drie stromen bij elkaar
 */

/**
 * @typedef {object} PortfolioSocietalValue
 * @property {Array<PortfolioProjectValue>} projects - Projecten met een bekend bedrag, hoogste eerst
 * @property {Array<string>} pending - Projecten waarvoor nog niets is uitgerekend
 * @property {number} total - De som over `projects`
 * @property {number|null} computedAt - Oudste rekenmoment in ms
 * @property {Array<string>} streams - Welke stromen in dit totaal zitten
 * @property {object|null} materialOutcomes - CO2 en energierekening van de projecten opgeteld; los van `total`
 * @property {number} materialOutcomesMissing - Projecten zonder berekende CO2/energierekening
 */

/**
 * De Maatschappelijke waarde van de projecten onder dit portfolio.
 *
 * Loopt via de Cloud Function `getPortfolioSocietalValue` en niet via
 * Firestore-hooks: een portfolio mag de collecties van zijn projecten niet
 * lezen, en een geweigerde read geeft in de browser geen fout maar een lege
 * lijst — de kaart zou dan stil op nul staan.
 * AI-CONTEXT: See docs/pitfalls.md#security-console-rules-missing-collection
 *
 * `data` is `null` zolang er geen antwoord is. Dat is iets anders dan een
 * antwoord zonder projecten, en de kaart moet die twee uit elkaar houden.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {boolean} [enabled] - Sla het ophalen over als dit geen portfolio is
 * @returns {{data: PortfolioSocietalValue|null, loading: boolean, error: Error|null}}
 */
const usePortfolioSocietalValue = (enabled = true) => {

    const [data, setData] = useState(/** @type {PortfolioSocietalValue|null} */ (null))
    const [loading, setLoading] = useState(Boolean(enabled))
    const [error, setError] = useState(/** @type {Error|null} */ (null))

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            return
        }

        let active = true
        setLoading(true)
        setError(null)

        fetchValue(client)
            .then((result) => {
                if (!active) return
                setData(result?.data || null)
            })
            .catch((err) => {
                // Luidruchtig: zonder deze regel is een geweigerde aanroep in
                // de console niet te onderscheiden van een portfolio zonder
                // projecten.
                // eslint-disable-next-line no-console
                console.error('usePortfolioSocietalValue:', err)
                if (active) setError(err)
            })
            .finally(() => { if (active) setLoading(false) })

        return () => { active = false }
    }, [enabled])

    return { data, loading, error }
}

export default usePortfolioSocietalValue
